/**
 * @module features/legal/TermsPage
 * @description Terms and Conditions document for RxDeviceGateway.
 * Governed by Roborosx Omni Tech Solutions LLP.
 */

import * as React from "react";
import { LegalLayout } from "./LegalLayout";
import { APP_NAME, ORG_NAME, ORG_SUPPORT_EMAIL } from "@/config/env";

export function TermsPage() {
  React.useEffect(() => {
    document.title = `Terms of Service — ${APP_NAME} | ${ORG_NAME}`;
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      title="Terms of Service"
      subtitle={`These terms govern the use of the ${APP_NAME} IoT Device Management and Gateway platform provided by ${ORG_NAME}.`}
      lastUpdated="October 24, 2026"
      badge="Terms of Service"
    >
      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          1. Acceptance of Terms
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          Welcome to <strong className="text-foreground">{APP_NAME}</strong>, an enterprise IoT edge and device management platform owned and operated by{" "}
          <strong className="text-foreground">{ORG_NAME}</strong> (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;).
        </p>
        <p className="text-muted-foreground leading-relaxed">
          By accessing, deploying, registering hardware on, or using the {APP_NAME} platform, associated APIs, administrative consoles, and gateway services (collectively, the &ldquo;Service&rdquo;), you (&ldquo;Customer&rdquo;, &ldquo;Administrator&rdquo;, or &ldquo;User&rdquo;) agree to be bound by these Terms of Service. If you are entering into this agreement on behalf of a company, partnership, or other legal entity, you represent that you have the authority to bind such entity to these terms.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          2. Scope of Service & Platform Capabilities
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          {APP_NAME} provides centralized fleet provisioning, lifecycle monitoring, concrete hardware subtype registration, and firmware auditability across edge display modules, gateway units, and field devices.
        </p>
        <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
          <li>
            <strong className="text-foreground">Fleet Operations:</strong> Device enrollment, status state-machine transitions (Provisioned, Active, Maintenance, Offline, Decommissioned), and cryptographic hardware inventorying.
          </li>
          <li>
            <strong className="text-foreground">Firmware Governance:</strong> Verification of operating system identities, SHA-256 build signatures, and immutable transition audit logs.
          </li>
          <li>
            <strong className="text-foreground">Access Controls:</strong> Role-based access control (Super Admin, Client, and Operator roles), WebAuthn/FIDO2 hardware passkeys, and Time-based One-Time Password (TOTP) two-factor authentication.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          3. Hardware Enrollment & MAC Address Governance
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          Device registration into the {APP_NAME} fleet requires adherence to hardware identification specifications established by {ORG_NAME}:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
          <li>
            <strong className="text-foreground">Hardware Uniqueness:</strong> Media Access Control (MAC) addresses and Registered Device Identifiers must be globally unique across the fleet. Re-registration of active or decommissioned hardware identifiers is strictly prohibited to preserve historical audit integrity; decommissioned devices must be reactivated through authorized workflows.
          </li>
          <li>
            <strong className="text-foreground">Subtype Specifications:</strong> For display module devices, valid operating system metadata (OS name, semver OS version, and cryptographic hash) are mandatory during provisioning and subsequent OTA firmware upgrades.
          </li>
          <li>
            <strong className="text-foreground">Bulk CSV Ingestion:</strong> Administrators utilizing bulk CSV enrollment are responsible for pre-validating file structures, enforcing row constraints (maximum 500 rows per batch), and ensuring no duplicate MAC addresses are present within batch files.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          4. Security, Credentials & Two-Factor Authentication
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          You are responsible for maintaining the confidentiality of your platform credentials. To protect enterprise infrastructure:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
          <li>
            Super Administrator accounts must enforce multi-factor authentication (TOTP or FIDO2/WebAuthn Passkeys).
          </li>
          <li>
            Passwords are cryptographically hashed using Argon2id with memory-hard parameters. Plaintext passwords are never stored, logged, or recoverable.
          </li>
          <li>
            You must promptly notify {ORG_NAME} at <a href={`mailto:${ORG_SUPPORT_EMAIL}`} className="text-foreground underline">{ORG_SUPPORT_EMAIL}</a> upon discovering any unauthorized access, compromised credentials, or security incidents involving your fleet.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          5. Acceptable Use Policy
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          You agree not to engage in, facilitate, or attempt any of the following activities:
        </p>
        <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
          <li>
            Probing, scanning, or testing the vulnerability of {APP_NAME} infrastructure, MQTT brokers, or API gateways without prior written authorization from {ORG_NAME}.
          </li>
          <li>
            Forging, spoofing, or tampering with hardware identifiers, MAC addresses, or cryptographic firmware hashes.
          </li>
          <li>
            Reverse-engineering, decompiling, or disassembling the proprietary protocols and gateway adapters of the Service.
          </li>
          <li>
            Overloading, flooding, or executing Denial of Service (DoS/DDoS) attacks against the central gateway or edge brokers.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          6. Intellectual Property & Ownership
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          All right, title, and interest in and to the {APP_NAME} platform—including software architecture, APIs, device registries, visual design systems, documentation, and trademarks—remain the exclusive property of <strong className="text-foreground">{ORG_NAME}</strong> and its licensors.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Customer retains full ownership of its hardware telemetry, device configurations, and proprietary application data ingested into the Service.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          7. Service Availability & Maintenance
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          {ORG_NAME} strives to provide a highly available (99.9% uptime target) cloud and gateway infrastructure. However, scheduled maintenance windows, emergency security patches, and telecommunication network disruptions may result in temporary service interruptions. Wherever feasible, notice of planned maintenance will be provided in advance through the administrator notification center.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          8. Limitation of Liability
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          To the maximum extent permitted by applicable law, in no event shall <strong className="text-foreground">{ORG_NAME}</strong>, its partners, officers, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, hardware downtime, or business interruption arising out of or in connection with the use or inability to use the Service.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          9. Governing Law & Dispute Resolution
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          These Terms shall be governed by and construed in accordance with the laws applicable to <strong className="text-foreground">{ORG_NAME}</strong>, without regard to conflict of law principles. Any legal suit, action, or proceeding arising out of or related to these Terms shall be instituted exclusively in the competent courts having jurisdiction over the registered office of {ORG_NAME}.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
          10. Contact Information
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          For any questions, legal notices, or operational inquiries regarding these Terms of Service, please contact:
        </p>
        <div className="rounded-xl border border-border/80 bg-card p-4 space-y-1 text-sm font-sans text-foreground">
          <p className="font-semibold">{ORG_NAME}</p>
          <p className="text-muted-foreground text-xs">Attn: Legal & Compliance Department</p>
          <p className="text-xs">
            Email:{" "}
            <a href={`mailto:${ORG_SUPPORT_EMAIL}`} className="underline text-foreground">
              {ORG_SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </section>
    </LegalLayout>
  );
}
